import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Bloom } from "../components/loader/index.js";
import { completeLogin } from "../lib/api/index.js";
import { queryKeys } from "../lib/query-keys.js";
import { documentTitle, usePageTitle } from "../lib/routes.js";
import { navigate } from "../lib/url-filters.js";
import { AuthShell } from "./auth-shell.js";

/**
 * Exchanges the token in the URL for a session.
 *
 * The one place useEffect is correct: this is not data fetching, it is a
 * one-shot side effect on arrival that must happen exactly once, and the §15a
 * ban is on using effects to load server state.
 *
 * The token is stripped from the address bar immediately. A live credential in
 * the URL ends up in browser history, in a Referer header on the next
 * navigation, and in any analytics that records paths.
 *
 * It wears the same shell as the sign-in screen. Arriving from an email client
 * used to land on a bare 480px column that looked like a different site, for
 * the two seconds before the app appeared behind it.
 */
export function CallbackScreen() {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  usePageTitle(documentTitle("Signing in"));

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (token === null) {
      setError("That link is missing its token.");
      return;
    }

    let cancelled = false;
    completeLogin(token)
      .then(async () => {
        // Replaced, so Back does not return to a spent link. Through the router,
        // which would otherwise still believe it was on the callback.
        navigate("/", { replace: true });
        await client.invalidateQueries({ queryKey: queryKeys.me() });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : "That link did not work.");
      });

    return () => {
      cancelled = true;
    };
  }, [client]);

  if (error !== null) {
    return (
      <AuthShell>
        <h1 className="auth-title">That link did not work</h1>
        <p className="auth-lede">{error}</p>

        <div className="auth-sent">
          A link is good for <strong>one sign-in</strong> and for{" "}
          <strong>15 minutes</strong>. If you opened it twice, or it sat in your inbox for
          a while, ask for a new one.
        </div>

        {/* A real navigation, not a button that only looks like one. */}
        <a className="button-secondary auth-second" href="/sign-in">
          Back to sign in
        </a>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Bloom size={56} motion="assemble" className="auth-bloom" />
      <h1 className="auth-title">Signing you in…</h1>
      <p className="auth-lede" role="status">
        Exchanging your link for a session. This takes a moment.
      </p>
    </AuthShell>
  );
}
